import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useRef } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useResizeObserver } from 'usehooks-ts';

import s from './character-counter.scss';

interface CharacterCounterProps<T extends FieldValues = FieldValues> {
  /**
   * RHF `control` object returned by `useForm`.
   */
  control: Control<T, any, any>;
  /**
   * Maximum number of characters. The counter turns red when exceeded.
   */
  limit: number;
  /**
   * Field path used to subscribe to the current value via `useWatch`.
   */
  name: FieldPath<T>;
}

/**
 * Displays a live `current / limit` character count for a text field.
 *
 * Subscribes to the field's value via `useWatch` so the count updates on
 * every keystroke. When the current length exceeds `limit` the counter is
 * styled in red to indicate the value is over the allowed length.
 *
 * The counter is purely visual — it does **not** enforce a hard `maxLength`.
 * Validation is the responsibility of the Zod schema.
 */
export const CharacterCounter = <T extends FieldValues>({
  control,
  limit,
  name,
}: CharacterCounterProps<T>) => {
  useStyles(s);

  const ref = useRef<HTMLSpanElement>(null);
  const value = useWatch({ control, name });
  const current = String(value ?? '').length;

  useResizeObserver({
    box: 'border-box',
    ref,
    onResize: ({ width }) => {
      if (width) {
        ref.current?.parentElement?.style.setProperty(
          '--character-counter-width',
          `${Math.ceil(width)}px`,
        );
      }
    },
  });

  return (
    <span
      ref={ref}
      className={classNames(s.root, { [s.over]: current > limit })}
      aria-live="polite"
    >
      {current} / {limit}
    </span>
  );
};
